import type { LensCardData, LensFilterValues } from '~/types/leLens';

export const AGENT_HUB_PAGE_ID = 'agent-hub';
export const ORDER_SOURCE = 'lux-analytics-prod.analytics.sales_with_refunds';
export const AGENT_SOURCE = 'lux-analytics-prod.analytics.agency_hub_agents';

export type Tab = 'orders' | 'agents' | 'finance';
export const TABS: ReadonlyArray<{ value: Tab; label: string }> = [
  { value: 'orders', label: 'Agent Hub | Orders' },
  { value: 'agents', label: 'Agent Hub | Agents' },
  { value: 'finance', label: 'Agent Hub | Finance Report' },
];

type Refunds = 'AR' | 'BR';
type Cadence = 'day' | 'week' | 'month' | 'quarter' | 'year';
export const CADENCES: ReadonlyArray<{ value: Cadence; label: string }> = [
  { value: 'day', label: 'Daily' },
  { value: 'week', label: 'Weekly' },
  { value: 'month', label: 'Monthly' },
  { value: 'quarter', label: 'Quarterly' },
  { value: 'year', label: 'Yearly' },
];

export type MeasureKey =
  | 'grossSales'
  | 'margin'
  | 'netMargin'
  | 'commission'
  | 'agentNett'
  | 'override'
  | 'packages'
  | 'nights'
  | 'orders'
  | 'agents'
  | 'agencies'
  | 'aov';
export type Measures = Record<MeasureKey, number | null>;

export const CHART_METRICS: ReadonlyArray<{ value: MeasureKey; label: string }> = [
  { value: 'grossSales', label: 'Gross Sales' },
  { value: 'margin', label: 'Margin' },
  { value: 'netMargin', label: 'Net Margin' },
  { value: 'orders', label: 'Orders' },
  { value: 'commission', label: 'Agent Comms.' },
  { value: 'agencies', label: 'Agencies' },
  { value: 'agents', label: 'Agents' },
  { value: 'aov', label: 'AoV' },
];

export type Split = 'agent_path' | 'product' | 'category' | 'offer_country' | 'vendor' | 'agency_country';
export const SPLITS: ReadonlyArray<{ value: Split; label: string }> = [
  { value: 'agent_path', label: 'Affiliation › Agency › Agent' },
  { value: 'product', label: 'Product' },
  { value: 'category', label: 'Category' },
  { value: 'offer_country', label: 'Offer country' },
  { value: 'vendor', label: 'Vendor' },
  { value: 'agency_country', label: 'Agency country' },
];

type SignupLevel = 'affiliates' | 'agencies' | 'agents';
export const SIGNUP_LEVELS: ReadonlyArray<{ value: SignupLevel; label: string }> = [
  { value: 'affiliates', label: 'Affiliates' },
  { value: 'agencies', label: 'Agencies' },
  { value: 'agents', label: 'Agents' },
];

/** Filters declared in svc-reporting's native-specs/agent-hub.json, keyed by filter id. */
export const FILTER_DEFINITIONS = [
  { key: 'affiliation', label: 'Affiliation', agents: true },
  { key: 'agency', label: 'Agency', agents: true },
  { key: 'agent', label: 'Agent', agents: true },
  { key: 'agency_country', label: 'Agency country', agents: true },
  { key: 'offer_country', label: 'Offer country', agents: false },
  { key: 'product', label: 'Product', agents: false },
  { key: 'category', label: 'Category', agents: false },
  { key: 'vendor', label: 'Vendor', agents: false },
] as const;
export type FilterKey = (typeof FILTER_DEFINITIONS)[number]['key'];

export type AgentHubFilters = {
  tab: Tab;
  from: string;
  to: string;
  refunds: Refunds;
  cadence: Cadence;
  chartMetric: MeasureKey;
  split: Split;
  /** How many levels of Affiliation › Agency › Agent the breakdown shows. */
  depth: 1 | 2 | 3;
  signupLevel: SignupLevel;
  showAffiliation: boolean;
  showAgencies: boolean;
  showAgents: boolean;
  selections: Record<FilterKey, Array<string>>;
};

// The workbook's saved window: P. Start Date is a week before yesterday, P. End Date yesterday.
const DEFAULT_WINDOW_DAYS = 7;

const sydneyDay = (date: Date) => new Intl.DateTimeFormat('en-CA', { timeZone: 'Australia/Sydney' }).format(date);
const addDays = (day: string, amount: number) =>
  new Date(Date.parse(`${day}T00:00:00Z`) + amount * 86400000).toISOString().slice(0, 10);
export const lastCompleteDay = (now = new Date()) => addDays(sydneyDay(now), -1);

export function defaultFilters(tab: Tab = 'orders', now = new Date()): AgentHubFilters {
  const to = lastCompleteDay(now);
  return {
    tab,
    from: addDays(to, 1 - DEFAULT_WINDOW_DAYS),
    to,
    refunds: 'AR',
    cadence: 'month',
    chartMetric: 'margin',
    split: 'agent_path',
    depth: 3,
    signupLevel: 'agents',
    showAffiliation: false,
    showAgencies: true,
    showAgents: true,
    selections: Object.fromEntries(FILTER_DEFINITIONS.map(({ key }) => [key, [] as Array<string>])) as Record<
      FilterKey,
      Array<string>
    >,
  };
}

const isDay = (value: string | null): value is string =>
  !!value && /^\d{4}-\d{2}-\d{2}$/.test(value) && new Date(`${value}T00:00:00Z`).toISOString().startsWith(value);

export function filtersError(filters: AgentHubFilters, now = new Date()): string | null {
  if (!isDay(filters.from) || !isDay(filters.to)) {
    return 'Choose valid dates in YYYY-MM-DD format.';
  }
  if (filters.from > filters.to) {
    return 'The start date must be on or before the end date.';
  }
  if (filters.to > lastCompleteDay(now)) {
    return `Data is complete to ${lastCompleteDay(now)}. Choose an earlier end date.`;
  }
  return null;
}

const ENUMS = {
  tab: TABS.map((item) => item.value),
  refunds: ['AR', 'BR'],
  cadence: CADENCES.map((item) => item.value),
  chartMetric: CHART_METRICS.map((item) => item.value),
  split: SPLITS.map((item) => item.value),
  signupLevel: SIGNUP_LEVELS.map((item) => item.value),
} as const;
const FLAGS = ['showAffiliation', 'showAgencies', 'showAgents'] as const;

export function parseFilters(params: URLSearchParams, now = new Date()): AgentHubFilters {
  const requestedTab = params.get('tab');
  const filters = defaultFilters(
    ENUMS.tab.includes(requestedTab as Tab) ? (requestedTab as Tab) : 'orders',
    now,
  ) as AgentHubFilters & Record<string, unknown>;
  for (const key of ['from', 'to'] as const) {
    if (isDay(params.get(key))) {
      filters[key] = params.get(key);
    }
  }
  for (const [key, values] of Object.entries(ENUMS)) {
    const value = params.get(key);
    if (value && (values as ReadonlyArray<string>).includes(value)) {
      filters[key] = value;
    }
  }
  const depth = Number(params.get('depth'));
  if ([1, 2, 3].includes(depth)) {
    filters.depth = depth as 1 | 2 | 3;
  }
  for (const key of FLAGS) {
    if (params.has(key)) {
      filters[key] = params.get(key) === '1';
    }
  }
  for (const { key } of FILTER_DEFINITIONS) {
    try {
      const value: unknown = JSON.parse(params.get(key) ?? 'null');
      if (Array.isArray(value) && value.every((item) => typeof item === 'string')) {
        filters.selections[key] = value;
      }
    } catch {
      // A malformed selection keeps the default.
    }
  }
  return filters;
}

export function writeFilters(filters: AgentHubFilters, current: URLSearchParams, now = new Date()): URLSearchParams {
  const params = new URLSearchParams(current);
  const defaults = defaultFilters(filters.tab, now);
  const set = (key: string, value: string, fallback: string) =>
    value === fallback ? params.delete(key) : params.set(key, value);
  params.set('tab', filters.tab);
  for (const key of ['from', 'to', 'refunds', 'cadence', 'chartMetric', 'split', 'signupLevel'] as const) {
    set(key, filters[key], defaults[key]);
  }
  set('depth', String(filters.depth), String(defaults.depth));
  for (const key of FLAGS) {
    set(key, filters[key] ? '1' : '0', defaults[key] ? '1' : '0');
  }
  for (const { key } of FILTER_DEFINITIONS) {
    const values = [...new Set(filters.selections[key])].sort();
    set(key, values.length ? JSON.stringify(values) : '', '');
  }
  return params;
}

export function lensFilters(filters: AgentHubFilters): LensFilterValues {
  const values: LensFilterValues = { period: { window: { from: filters.from, to: filters.to }, comparison: null } };
  for (const { key } of FILTER_DEFINITIONS) {
    if (filters.selections[key].length) {
      values[key] = filters.selections[key];
    }
  }
  return values;
}

export type CardResult = { rows: Array<Record<string, unknown>>; truncated: boolean; card: LensCardData };
export type CardProblem = { problem: string };
export const isProblem = (result: CardResult | CardProblem): result is CardProblem => 'problem' in result;

export function readCard(cards: ReadonlyArray<LensCardData>, cardId: string): CardResult | CardProblem {
  const card = cards.find((item) => item.cardId === cardId);
  if (!card) {
    return { problem: 'This section is still loading.' };
  }
  if (card.status === 'ready' || card.status === 'empty') {
    return { rows: card.rows ?? [], truncated: Boolean(card.truncated), card };
  }
  return { problem: `This section could not load. ${card.message ?? ''}`.trim() };
}

const num = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = Number(typeof value === 'object' && value && 'value' in value ? value.value : value);
  return Number.isFinite(parsed) ? parsed : null;
};
const text = (value: unknown): string | null =>
  value === null || value === undefined
    ? null
    : String(typeof value === 'object' && 'value' in value ? value.value : value);
const divide = (a: number | null, b: number | null) => (a === null || b === null || b === 0 ? null : a / b);
const minus = (a: number | null, b: number | null) => (a === null ? null : a - (b ?? 0));

const ORDER_SUMS = [
  'gross_sales_aud',
  'refund_aud',
  'margin_aud',
  'margin_refund_aud',
  'commission_aud',
  'commission_refund_aud',
  'packages',
  'packages_refund',
  'nights',
  'nights_refund',
  'override_aud',
  'override_refund_aud',
] as const;
type OrderSums = Record<(typeof ORDER_SUMS)[number], number | null>;
type OrderCounts = { orders: number | null; agents: number | null; agencies: number | null };
const NO_COUNTS: OrderCounts = { orders: null, agents: null, agencies: null };

export function sumsOf(rows: ReadonlyArray<Record<string, unknown>>): OrderSums {
  return Object.fromEntries(
    ORDER_SUMS.map((key) => {
      const known = rows.map((row) => num(row[key])).filter((value): value is number => value !== null);
      return [key, known.length ? known.reduce((total, value) => total + value, 0) : null];
    }),
  ) as OrderSums;
}

const countsOf = (row: Record<string, unknown> | undefined): OrderCounts =>
  row ? { orders: num(row.orders), agents: num(row.agents), agencies: num(row.agencies) } : NO_COUNTS;

/** AR nets each refund-side amount from its sale side. AOV stays before refunds, as in Tableau. */
export function measuresOf(sums: OrderSums, counts: OrderCounts, refunds: Refunds): Measures {
  const ar = refunds === 'AR';
  const grossSales = ar ? minus(sums.gross_sales_aud, sums.refund_aud) : sums.gross_sales_aud;
  const margin = ar ? minus(sums.margin_aud, sums.margin_refund_aud) : sums.margin_aud;
  const commission = ar ? minus(sums.commission_aud, sums.commission_refund_aud) : sums.commission_aud;
  return {
    grossSales,
    margin,
    netMargin: minus(margin, commission),
    commission,
    agentNett: minus(grossSales, commission),
    override: ar ? minus(sums.override_aud, sums.override_refund_aud) : sums.override_aud,
    packages: ar ? minus(sums.packages, sums.packages_refund) : sums.packages,
    nights: ar ? minus(sums.nights, sums.nights_refund) : sums.nights,
    orders: counts.orders,
    agents: counts.agents,
    agencies: counts.agencies,
    aov: divide(sums.gross_sales_aud, counts.orders),
  };
}

export function kpisOf(
  sums: CardResult | CardProblem,
  counts: CardResult | CardProblem,
  refunds: Refunds,
): { current: Measures; previous: Measures | null } | null {
  if (isProblem(sums) || isProblem(counts)) {
    return null;
  }
  const previousSums = sums.card.comparison?.rows;
  const previousCounts = counts.card.comparison?.rows;
  return {
    current: measuresOf(sumsOf(sums.rows), countsOf(counts.rows[0]), refunds),
    previous:
      previousSums && previousCounts ? measuresOf(sumsOf(previousSums), countsOf(previousCounts[0]), refunds) : null,
  };
}

export const change = (current: number | null, previous: number | null | undefined) =>
  current === null || previous === null || previous === undefined || previous === 0 ? null : current / previous - 1;

const day = (row: Record<string, unknown>, key: string) => (text(row[key]) ?? '').slice(0, 10);

/** Weeks start on Monday, as in the workbook. */
export function bucketOf(date: string, cadence: Cadence): string {
  if (cadence === 'day') {
    return date;
  }
  if (cadence === 'week') {
    return addDays(date, -((new Date(`${date}T00:00:00Z`).getUTCDay() + 6) % 7));
  }
  const [year, month] = date.split('-').map(Number);
  if (cadence === 'month') {
    return `${year}-${String(month).padStart(2, '0')}-01`;
  }
  if (cadence === 'quarter') {
    return `${year}-${String(Math.floor((month - 1) / 3) * 3 + 1).padStart(2, '0')}-01`;
  }
  return `${year}-01-01`;
}

function bucketLabel(bucket: string, cadence: Cadence): string {
  const date = new Date(`${bucket}T00:00:00Z`);
  const month = date.toLocaleString('en-AU', { month: 'short', timeZone: 'UTC' });
  const year = String(date.getUTCFullYear());
  if (cadence === 'year') {
    return year;
  }
  if (cadence === 'quarter') {
    return `${year} Q${Math.floor(date.getUTCMonth() / 3) + 1}`;
  }
  if (cadence === 'month') {
    return `${month} ${year.slice(2)}`;
  }
  return `${date.getUTCDate()} ${month} ${year.slice(2)}${cadence === 'week' ? ' w/c' : ''}`;
}

export type TrendPoint = { bucket: string; label: string; measures: Measures; current: boolean };

/**
 * Daily sums rolled up to the cadence, joined to that cadence's own distinct counts (which
 * cannot be added up from days). A bucket is "current" when it overlaps the selected window.
 */
export function trendOf(
  daily: CardResult,
  counts: CardResult | CardProblem,
  filters: Pick<AgentHubFilters, 'cadence' | 'refunds' | 'from' | 'to'>,
): Array<TrendPoint> {
  const groups = new Map<string, Array<Record<string, unknown>>>();
  for (const row of daily.rows) {
    const bucket = bucketOf(day(row, 'transaction_date_sydt'), filters.cadence);
    groups.set(bucket, [...(groups.get(bucket) ?? []), row]);
  }
  const countRows = new Map(
    (isProblem(counts) ? [] : counts.rows).map((row) => [
      bucketOf(day(row, 'transaction_date_sydt'), filters.cadence),
      row,
    ]),
  );
  const firstBucket = bucketOf(filters.from, filters.cadence);
  return [...groups.keys()].sort().map((bucket) => ({
    bucket,
    label: bucketLabel(bucket, filters.cadence),
    measures: measuresOf(sumsOf(groups.get(bucket) ?? []), countsOf(countRows.get(bucket)), filters.refunds),
    current: bucket >= firstBucket && bucket <= filters.to,
  }));
}

export const PATH_SEPARATOR = '\u001f';

export type BreakdownRow = { key: string; parts: Array<string>; measures: Measures };

/**
 * Breakdown rows for the chosen split. Affiliation › Agency › Agent comes back at agent grain and
 * rolls up here: an order has one agent, so distinct orders add up exactly to agency and affiliation.
 */
export function breakdownOf(
  sums: CardResult,
  orders: CardResult | CardProblem,
  split: Split,
  depth: number,
  refunds: Refunds,
): Array<BreakdownRow> {
  const orderCounts = new Map(
    (isProblem(orders) ? [] : orders.rows).map((row) => [text(row[split]) ?? 'Unknown', num(row.orders)]),
  );
  const groups = new Map<
    string,
    { parts: Array<string>; rows: Array<Record<string, unknown>>; orders: number | null }
  >();
  for (const row of sums.rows) {
    const value = text(row[split]) ?? 'Unknown';
    const parts = split === 'agent_path' ? value.split(PATH_SEPARATOR).slice(0, depth) : [value];
    const key = parts.join(PATH_SEPARATOR);
    const group = groups.get(key) ?? { parts, rows: [], orders: null };
    group.rows.push(row);
    const rowOrders = orderCounts.get(value);
    if (rowOrders !== undefined && rowOrders !== null) {
      group.orders = (group.orders ?? 0) + rowOrders;
    }
    groups.set(key, group);
  }
  return [...groups.entries()].map(([key, group]) => ({
    key,
    parts: group.parts,
    measures: measuresOf(sumsOf(group.rows), { ...NO_COUNTS, orders: group.orders }, refunds),
  }));
}

export const FINANCE_FIELDS = [
  'transactionDate',
  'affiliation',
  'agency',
  'agentId',
  'bookingNumber',
  'itemId',
  'orderId',
  'offerId',
  'product',
  'category',
  'vendor',
  'offerName',
  'transactionType',
  'costCurrency',
  'orderStatus',
] as const;
export type FinanceRow = Record<(typeof FINANCE_FIELDS)[number], string> & {
  grossSales: number | null;
  costSales: number | null;
  margin: number | null;
};

export function financeRowsOf(result: CardResult): Array<FinanceRow> {
  return result.rows
    .map((row) => {
      const parts = (text(row.item_line) ?? '').split(PATH_SEPARATOR);
      return {
        ...(Object.fromEntries(FINANCE_FIELDS.map((field, index) => [field, parts[index] ?? ''])) as Record<
          (typeof FINANCE_FIELDS)[number],
          string
        >),
        grossSales: num(row.gross_sales_aud),
        costSales: num(row.cost_sales_aud),
        margin: num(row.margin_aud),
      };
    })
    .sort((a, b) => b.transactionDate.localeCompare(a.transactionDate));
}

type SignupCounts = Record<SignupLevel, number | null>;
const signupsOf = (row: Record<string, unknown> | undefined): SignupCounts => ({
  affiliates: row ? num(row.affiliate_signups) : null,
  agencies: row ? num(row.agency_signups) : null,
  agents: row ? num(row.agent_signups) : null,
});

type AgentKpis = {
  signups: SignupCounts;
  previousSignups: SignupCounts | null;
  active: SignupCounts;
  churned: SignupCounts;
};

export function agentKpisOf(signups: CardResult | CardProblem, status: CardResult | CardProblem): AgentKpis | null {
  if (isProblem(signups) || isProblem(status)) {
    return null;
  }
  const row = status.rows[0] ?? {};
  const previous = signups.card.comparison?.rows;
  return {
    signups: signupsOf(signups.rows[0]),
    previousSignups: previous ? signupsOf(previous[0]) : null,
    active: {
      affiliates: num(row.active_affiliates),
      agencies: num(row.active_agencies),
      agents: num(row.active_agents),
    },
    churned: {
      affiliates: num(row.churned_affiliates),
      agencies: num(row.churned_agencies),
      agents: num(row.churned_agents),
    },
  };
}

export type SignupPoint = { bucket: string; label: string; added: SignupCounts; cumulative: SignupCounts };

/**
 * Sign-ups per cadence bucket and their running total. Each agency and affiliation is counted
 * once, on its first sign-up day, so days add up to buckets and totals.
 */
export function signupTrendOf(result: CardResult, cadence: Cadence): Array<SignupPoint> {
  const buckets = new Map<string, SignupCounts>();
  for (const row of result.rows) {
    const bucket = bucketOf(day(row, 'signup_date_syd'), cadence);
    const counts = signupsOf(row);
    const total = buckets.get(bucket) ?? { affiliates: 0, agencies: 0, agents: 0 };
    buckets.set(bucket, {
      affiliates: (total.affiliates ?? 0) + (counts.affiliates ?? 0),
      agencies: (total.agencies ?? 0) + (counts.agencies ?? 0),
      agents: (total.agents ?? 0) + (counts.agents ?? 0),
    });
  }
  const running: SignupCounts = { affiliates: 0, agencies: 0, agents: 0 };
  return [...buckets.keys()].sort().map((bucket) => {
    const added = buckets.get(bucket);
    for (const level of ['affiliates', 'agencies', 'agents'] as const) {
      running[level] = (running[level] ?? 0) + (added[level] ?? 0);
    }
    return { bucket, label: bucketLabel(bucket, cadence), added, cumulative: { ...running } };
  });
}

export type AgentRow = {
  key: string;
  affiliation: string;
  agency: string;
  agent: string;
  signupDate: string;
  firstOrderDate: string;
  lastOrderDate: string;
  grossSales: number | null;
  margin: number | null;
  orders: number | null;
  items: number | null;
  commission: number | null;
};

const add = (a: number | null, b: number | null) => (a === null ? b : b === null ? a : a + b);
const earliest = (a: string, b: string) => (!a ? b : !b ? a : a < b ? a : b);

/** Hidden levels are blanked and merged, as the workbook's Show Affiliation?/Agencies?/Agents? do. */
export function agentRowsOf(
  result: CardResult,
  show: Pick<AgentHubFilters, 'showAffiliation' | 'showAgencies' | 'showAgents'>,
): Array<AgentRow> {
  const groups = new Map<string, AgentRow>();
  for (const row of result.rows) {
    const [affiliation = '', agency = '', agent = '', , signup = '', first = '', last = ''] = (
      text(row.agent_line) ?? ''
    ).split(PATH_SEPARATOR);
    const shown = {
      affiliation: show.showAffiliation ? affiliation : '',
      agency: show.showAgencies ? agency : '',
      agent: show.showAgents ? agent : '',
    };
    const key = [shown.affiliation, shown.agency, shown.agent].join(PATH_SEPARATOR);
    const current = groups.get(key);
    groups.set(key, {
      key,
      ...shown,
      signupDate: earliest(current?.signupDate ?? '', signup),
      firstOrderDate: earliest(current?.firstOrderDate ?? '', first),
      lastOrderDate: [current?.lastOrderDate ?? '', last].sort().pop() ?? '',
      grossSales: add(current?.grossSales ?? null, num(row.gross_sales_aud)),
      margin: add(current?.margin ?? null, num(row.margin_aud)),
      orders: add(current?.orders ?? null, num(row.orders)),
      items: add(current?.items ?? null, num(row.items)),
      commission: add(current?.commission ?? null, num(row.commission_aud)),
    });
  }
  return [...groups.values()];
}

export function periodLabel(card: LensCardData | undefined, fallback = ''): string {
  return card?.period?.label?.current ?? fallback;
}

export function comparisonLabel(card: LensCardData | undefined): string | null {
  return card?.period?.label?.comparison ?? null;
}

export function csvOf(lines: Array<Array<unknown>>): string {
  // Leading =, +, - or @ would run as a formula in a spreadsheet, so text cells starting with one get a quote.
  const cell = (value: unknown) => {
    const raw = value === null || value === undefined ? '' : String(value);
    const safe = typeof value === 'string' && /^[=+\-@]/.test(raw) ? `'${raw}` : raw;
    return `"${safe.replace(/"/g, '""')}"`;
  };
  return '﻿' + lines.map((line) => line.map(cell).join(',')).join('\r\n');
}
